"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, MapPin, ChevronRight, Star, Shield, Clock } from "lucide-react";

const regions = [
  {
    id: "gulf",
    label: "Gulf Visas",
    title: "Explore the Golden Sands of the Gulf",
    subtitle: "Premium Visa Services for GCC Countries",
    description: "Expert assistance for UAE, Bahrain, Qatar, Kuwait, and more. We ensure a seamless entry to the Middle East's most dynamic economies with fast processing and expert guidance.",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&q=80&w=1200",
    visas: ["UAE Visit Visa", "Bahrain E-Visa", "Qatar Tourist Visa", "Kuwait Visit Visa", "Oman Visit Visa", "Saudi Arabia E-Visa"],
    stats: "Fast Processing (2-5 Days)",
    color: "#000000"
  },
  {
    id: "western",
    label: "Europe & Western",
    title: "Your Gateway to the Western World",
    subtitle: "Trusted Schengen & Western Visa Solutions",
    description: "Navigate the complexities of Schengen, UK, USA, Canada, and Australia visas with our proven track record of success. We handle all documentation and interview prep.",
    image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?auto=format&fit=crop&q=80&w=1200",
    visas: ["Schengen Visa", "United Kingdom (UK)", "United States (USA)", "Canada Visitor", "Australia Visit", "Europe Work Permit"],
    stats: "98% Success Rate",
    color: "#000000"
  },
  {
    id: "asia",
    label: "Asia Visas",
    title: "Discover the Wonders of Asia",
    subtitle: "Explore Exotic Destinations Across the Continent",
    description: "From the bustling streets of Singapore to the serene temples of Thailand, we handle all your Asian travel requirements with ease and reliability.",
    image: "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&q=80&w=1200",
    visas: ["Singapore E-Visa", "Thailand Tourist", "Malaysia (eNTRI)", "Vietnam E-Visa", "Japan Visitor", "South Korea K-ETA"],
    stats: "Easy Documentation",
    color: "#000000"
  }
];

const regionAnchorMap = {
  gulf: "gulf",
  western: "europe",
  asia: "asia",
};

export const DiscoverSolution = () => {
  const [activeTab, setActiveTab] = useState(0);
  const router = useRouter();

  const currentRegion = regions[activeTab];

  return (
    <main
      style={{
        background: "#ffffff",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(20px, 5vw, 100px) clamp(16px, 3vw, 40px)",
        boxSizing: "border-box",
        overflow: "hidden"
      }}
    >
      <div style={{ maxWidth: "1400px", width: "100%", position: "relative" }}>
        
        {/* SECTION HEADER */}
        <div style={{ textAlign: "center", marginBottom: "clamp(30px, 4vw, 60px)" }}>
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{
              color: "#dea600",
              textTransform: "uppercase",
              letterSpacing: "0.2em",
              fontSize: "clamp(12px, 1vw, 14px)",
              fontWeight: 600,
              display: "block",
              marginBottom: "12px"
            }}
          >
            Our Visa Solutions
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{
              fontFamily: "var(--font-montserrat), sans-serif",
              fontSize: "clamp(28px, 5vw, 48px)",
              fontWeight: 600,
              lineHeight: "1.3",
              color: "#1a1a1a",
              margin: 0
            }}
          >
            Find Your Next Destination
          </motion.h1>
        </div>

        {/* TAB NAVIGATION */}
        <div 
          className="discover-tabs"
          style={{ 
            display: "flex", 
            justifyContent: "center", 
            gap: "clamp(10px, 2vw, 20px)",
            marginBottom: "clamp(40px, 5vw, 80px)",
            flexWrap: "wrap"
          }}
        >
          {regions.map((region, idx) => (
            <button
              key={region.id}
              className="discover-tab"
              onClick={() => setActiveTab(idx)}
              style={{
                padding: "12px 30px",
                borderRadius: "50px",
                border: "none",
                background: activeTab === idx ? "#1a1a1a" : "#f5f5f5",
                color: activeTab === idx ? "#fff" : "#666",
                cursor: "pointer",
                fontSize: "clamp(14px, 1.1vw, 16px)",
                fontWeight: 500,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: activeTab === idx ? "0 10px 20px rgba(0,0,0,0.1)" : "none"
              }}
            >
              <Globe size={18} />
              {region.label}
            </button>
          ))}
        </div>

        {/* MAIN CONTENT GRID */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "clamp(30px, 5vw, 80px)",
              alignItems: "center"
            }}
          >
            {/* LEFT: Image & Badges */}
            <div style={{ position: "relative" }}>
              <motion.div
                layoutId="region-image"
                style={{
                  height: "clamp(400px, 60vh, 650px)",
                  borderRadius: "30px",
                  overflow: "hidden",
                  boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
                  position: "relative"
                }}
              >
                <img
                  src={currentRegion.image}
                  alt={currentRegion.label}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <div 
                  style={{ 
                    position: "absolute", 
                    inset: 0, 
                    background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent)" 
                  }} 
                />
                
                {/* Floating Info Badges */}
                <div style={{ position: "absolute", bottom: "30px", left: "30px", right: "30px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ background: "rgba(255,255,255,0.9)", padding: "8px 16px", borderRadius: "12px", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Shield size={16} color={currentRegion.color} />
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a" }}>{currentRegion.stats}</span>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.9)", padding: "8px 16px", borderRadius: "12px", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Clock size={16} color={currentRegion.color} />
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a" }}>Expert Support</span>
                  </div>
                </div>
              </motion.div>

              {/* Decorative Element */}
              <div 
                style={{ 
                  position: "absolute", 
                  top: "-20px", 
                  right: "-20px", 
                  width: "120px", 
                  height: "120px", 
                  border: `2px dashed ${currentRegion.color}`, 
                  borderRadius: "50%", 
                  zIndex: -1,
                  opacity: 0.3
                }} 
              />
            </div>

            {/* RIGHT: Text & Visa List */}
            <div>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{
                  fontSize: "clamp(18px, 2vw, 20px)",
                  fontWeight: 600,
                  marginBottom: "10px",
                  color: "#1a1a1a",
                  lineHeight: 1.2
                }}
              >
                {currentRegion.title}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{
                  fontSize: "clamp(14px, 1.2vw, 16px)",
                  color: "#666",
                  marginBottom: "30px",
                  lineHeight: 1.6
                }}
              >
                {currentRegion.description}
              </motion.p>

              {/* Visa Items Grid */}
              <div 
                style={{ 
                  display: "grid", 
                  gridTemplateColumns: "1fr 1fr", 
                  gap: "16px",
                  marginBottom: "40px"
                }}
              >
                {currentRegion.visas.map((visa, vIdx) => (
                  <motion.div
                    key={visa}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + (vIdx * 0.05) }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "12px 20px",
                      background: "#f9f9f9",
                      borderRadius: "12px",
                      border: "1px solid #eee",
                      cursor: "default"
                    }}
                    whileHover={{ scale: 1.02, backgroundColor: "#fff", boxShadow: "0 5px 15px rgba(0,0,0,0.05)" }}
                  >
                    <div style={{ color: currentRegion.color }}>
                      <Star size={16} fill="currentColor" />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>{visa}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTA */}
              <motion.button
                onClick={() => router.push(`/services#${regionAnchorMap[currentRegion.id]}`)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  background: "#1a1a1a",
                  color: "#fff",
                  padding: "16px 40px",
                  borderRadius: "50px",
                  border: "none",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
                }}
              >
                Enquire Now
                <ChevronRight size={20} />
              </motion.button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <style>{`
        @media (max-width: 768px) {
          main {
            padding: 0px 0px;
          }

          .discover-tabs {
            justify-content: flex-start !important;
            gap: 10px !important;
          }

          .discover-tab {
            flex: 0 1 calc(50% - 8px) !important;
            justify-content: center !important;
            padding: 10px 14px !important;
            font-size: 13px !important;
          }

          .visa-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </main>
  );
};

export default DiscoverSolution;
